import { AccountingType } from 'v2-types/order';
import { z } from 'zod';
import validations from 'utils/validations';

export const schemaModalAddEntry = z.object({
  description: validations.requiredString('required'),
  entryDate: z.any().refine(
    (value) => value === '' || value === null || value === undefined
      || !Number.isNaN(new Date(value as any).getTime()),
    'error-date',
  ),
  targets: z.array(z.object({
    checked: z.boolean().optional(),
    amount: z.coerce.number().optional(),
  }).passthrough()).default([]).refine(
    (targets) => targets.some((target) => target.checked === true),
    { message: '' },
  ),
  posted: validations.requiredBoolean('required'),
}).passthrough();

export const schemaModalEditEntry = () => z.object({
  description: validations.requiredString('required'),
  amount_override: z.coerce.number().positive('error-amount').optional(),
}).passthrough();

const conditionFilterDisbursments = (kindEntry: string[], entry: AccountingType) => {
  if (entry.code === "net_proceeds" && kindEntry.includes('proceeds')) return false;
  return (
    (kindEntry.includes(entry.kind) && (kindEntry.length === 3 ? entry.posted : true))
  );
};

export const filterEntriesToDisbursment = (accounting: AccountingType[], kindEntry: string[]) => {
  const filteredDisbursments = accounting
    .filter((entry) => conditionFilterDisbursments(kindEntry, entry))
    .sort((a, b) => a.description.localeCompare(b.description))
    .map((entry) => ({
      ...entry,
      involved_parties: entry.involved_parties.sort((a, b) => {
        const result = a.kind.localeCompare(b.kind);
        if (result === 0) return a.name.localeCompare(b.name);
        return result;
      })
    }));

  const accountingFiltered = accounting
    .map((entry) => {
      // Show all parties with non-zero amounts — let the modal filter by party role if needed
      const relevantParties = entry.involved_parties.filter(p => p.amount !== 0);

      // Preserve the raw description here. A ledger entry can have multiple parties, so this
      // transform cannot safely infer which trailing "to <name>" (if any) belongs to a row.
      // Change 5 removes only an exact, row-specific `to ${entry.name}` suffix at render time.
      const baseDesc = entry.description.trim();

      // Runtime API payloads may use empty letter / nullish number despite non-nullable TS declarations.
      // Explicit checks intentionally treat number = 0 as a valid number.
      const hasLetterAndNumber = entry.letter !== ''
        && entry.letter !== null
        && entry.letter !== undefined
        && entry.number !== null
        && entry.number !== undefined;

      return {
        ...entry,
        description: hasLetterAndNumber
          ? `${entry.letter}-${entry.number} ${baseDesc}`
          : baseDesc, // No prefix if letter/number missing — still selectable in modal
        involved_parties: relevantParties,
      };
    })
    .filter((entry) => entry.involved_parties.length > 0)
    // Sort by letter → number → description. Prefixed entries sort first (grouped), non-prefixed fall to end (alphabetical).
    .sort((a, b) => {
      // Explicit checks keep number = 0 valid and align with the submission-time guard.
      const hasPrefixA = a.letter !== '' && a.letter !== null && a.letter !== undefined
        && a.number !== null && a.number !== undefined;
      const hasPrefixB = b.letter !== '' && b.letter !== null && b.letter !== undefined
        && b.number !== null && b.number !== undefined;

      if (hasPrefixA && hasPrefixB) {
        // Both have prefix: sort by letter, then number, then description
        const letterCmp = a.letter.localeCompare(b.letter);
        if (letterCmp !== 0) return letterCmp;

        const numA = a.number ?? Infinity;
        const numB = b.number ?? Infinity;
        if (numA !== numB) return numA < numB ? -1 : 1;
      } else {
        // One or both missing prefix: prefixed entries always come first
        if (hasPrefixA && !hasPrefixB) return -1;
        if (!hasPrefixA && hasPrefixB) return 1;
      }

      // Both without prefix (or same status): sort by full description string
      return a.description.localeCompare(b.description);
    });

  return {
    filteredDisbursments,
    accountingFiltered,
  };
};

export const filteredAccountingLinks = (accounting: AccountingType[], kindEntry: string[]) => {
  return accounting.filter((accEntry) => {
    // Note: letter+number guard and J exclusion moved to submission time.
    // Entries shown in the modal list may lack letter/number fields — they're
    // selectable, but handleCreationEntry validates before posting.
    // Row-level party-role filtering happens in TableCharges (Payee rows for
    // Disbursements, Payer rows for Receipts/NetFunded).

    const kindIncludes = kindEntry.includes.bind(kindEntry);

    if (kindIncludes('receipt')) {
      // Show entries that have at least one Payer (escrow inflow)
      if (accEntry.involved_parties.some(p => p.kind === 'Payer')) return true;
    }

    if (kindIncludes('disbursement')) {
      // Show entries that have at least one Payee (escrow outflow)
      if (accEntry.involved_parties.some(p => p.kind === 'Payee')) return true;
    }

    if (kindIncludes('netfunded')) {
      // NetFunded starts from every entry surviving shared non-zero-party preprocessing.
      // TableCharges then renders only remaining Payer + Lender party rows; entries without one
      // are not shown as disabled rows.
      return true;
    }

    if (kindIncludes('proceeds')) {
      // Proceeds: show entries with at least one Payee (escrow outflow for proceeds distribution)
      // Note: This branch is defensive dead code — the Proceeds tab never opens the Add Entry modal
      // (disbursments.tsx line 154 excludes it). Included for future-proofing if behavior changes.
      return accEntry.involved_parties.some(p => p.kind === 'Payee');
    }

    return false; // No matching kind found
  });
};
